import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonSkeletonText } from '@ionic/react';
import { addBusinessDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import CircleIconButton from '@components/buttons/CircleIconButton';
import styles from '@components/carePlan/CarePlan.module.css';
import ItemBrowser from '@components/carePlan/ItemBrowser';
import BaseErrors from '@components/fields/BaseErrors';
import DateInput from '@components/fields/DateInput';
import RichTextArea from '@components/fields/RichTextArea';
import SearchInput from '@components/fieldsNative/SearchInput';
import Form from '@components/forms/Form';
import BodyCopy from '@components/typography/BodyCopy';
import useCreateCarePlanActionItem from '@hooks/mutations/useCreateCarePlanActionItem';
import useCreateGoal from '@hooks/mutations/useCreateGoal';
import useCreateTask from '@hooks/mutations/useCreateTask';
import { Opener } from '@hooks/useOpener';
import useSearchCarePlanTaskTemplates from '@hooks/useSearchCarePlanTaskTemplates';
import useSearchGoalTemplates from '@hooks/useSearchGoalTemplates';
import {
  GetJourneyWithPlanQuery,
  GoalItemTypeEnum,
  GoalUpdateDocument,
  GoalUpdateMutation,
  GoalUpdateMutationVariables,
  UserTypeEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { stripText } from '@utils/stringUtils';

type FormData = {
  dueTime?: string | null | undefined;
  title: string;
};

type Props = {
  canTemplate?: boolean;
  defaultValue?: string;
  goal?: GetJourneyWithPlanQuery['journey']['goals'][0] | null;
  itemBrowserOpener: Opener | null;
  journey: GetJourneyWithPlanQuery['journey'];
  onCancel: () => void;
  onSuccess: (id: string) => void;
  templatePlaceholderKey: string;
  type?: GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM | GoalItemTypeEnum.TASK;
};

const GoalEditorTextField = ({
  canTemplate = true,
  defaultValue,
  goal,
  itemBrowserOpener,
  journey,
  onCancel,
  onSuccess,
  templatePlaceholderKey,
  type
}: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [addingGoal, setAddingGoal] = useState(false);

  const goalCreate = useCreateGoal({ journey });

  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [editorRefresh, setEditorRefresh] = useState(false);

  const threeBusinessDaysFromNow = addBusinessDays(new Date(), 3).toISOString().slice(0, 10);

  const defaultValues: FormData = {
    dueTime: type === GoalItemTypeEnum.TASK ? threeBusinessDaysFromNow : undefined,
    title: defaultValue ?? ''
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      dueTime: yup.lazy(() => {
        if (type === GoalItemTypeEnum.TASK) {
          return yup.string().required(intl.formatMessage({ id: 'errors.task.dueTime.blank' }));
        } else {
          return yup.string().nullable();
        }
      }),
      title: yup.string().required(intl.formatMessage({ id: 'errors.goal.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const title = form.watch('title');

  useEffect(() => {
    if (title === '<p></p>') {
      setSubmitDisabled(true);
    } else if (submitDisabled) {
      setSubmitDisabled(false);
    }
  }, [submitDisabled, title]);

  const [updateGoal] = useMutation<GoalUpdateMutation, GoalUpdateMutationVariables>(GoalUpdateDocument);

  const handleAddTemplate = useCallback((id: string, title: string) => {
    form.setValue('title', title ?? '', { shouldTouch: true, shouldValidate: true });
    setEditorRefresh(value => !value);
    setTemplateId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchCarePlanTaskTemplates = useSearchCarePlanTaskTemplates(
    type === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM ? UserTypeEnum.client : UserTypeEnum.expert,
    handleAddTemplate
  );

  const searchGoalTemplates = useSearchGoalTemplates(handleAddTemplate);

  const searchTemplates = type ? searchCarePlanTaskTemplates : searchGoalTemplates;

  const createCarePlanActionItem = useCreateCarePlanActionItem(journey);
  const createTask = useCreateTask(journey);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const cleanTitle = stripText(title);
    const dueTime = form.getValues().dueTime;

    if (goal) {
      if (type === GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM) {
        return createCarePlanActionItem({
          variables: {
            assignmentIds: [journey.owner.id],
            carePlanTaskTemplateId: templateId,
            goalId: goal.id,
            title
          }
        }).then(response => {
          handleMutationResponse<FormData>({
            data: response.data?.carePlanActionItemCreate,
            intl,
            onSuccess: () => {
              onSuccess(response.data?.carePlanActionItemCreate?.carePlanActionItem?.id ?? '');
              form.reset();
            },
            setBaseErrors,
            setError: form.setError,
            txBase: 'errors.carePlanActionItem'
          });
        });
      } else if (type === GoalItemTypeEnum.TASK && dueTime) {
        return createTask({
          variables: {
            clientVisible: true,
            dueTime,
            expertId: journey.primaryExpert?.id ?? '',
            goalId: goal.id,
            journeyId: journey.id,
            title: cleanTitle
          }
        }).then(response => {
          handleMutationResponse<FormData>({
            data: response.data?.taskCreate,
            intl,
            onSuccess: () => {
              onSuccess(response.data?.taskCreate?.task?.id ?? '');
              form.reset();
            },
            setBaseErrors,
            setError: form.setError,
            txBase: 'errors.task'
          });
        });
      } else {
        return updateGoal({
          variables: {
            id: goal.id,
            title: cleanTitle
          }
        }).then(response => {
          handleMutationResponse<FormData>({
            data: response.data?.goalUpdate,
            intl,
            onSuccess: () => {
              onSuccess(response.data?.goalUpdate?.goal?.id ?? '');
              form.reset();
            },
            setBaseErrors,
            setError: form.setError,
            txBase: 'errors.goal'
          });
        });
      }
    } else {
      setAddingGoal(true);
      goalCreate({
        draft: true,
        goalTemplateId: templateId,
        journeyId: journey.id,
        title: cleanTitle
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response?.goalCreate,
          intl,
          onSuccess: () => {
            if (response?.goalCreate?.goal) {
              setAddingGoal(false);
              onSuccess(response.goalCreate.goal.id);
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.goal'
        });
      });
    }
  }, [
    createCarePlanActionItem,
    createTask,
    form,
    goal,
    goalCreate,
    intl,
    journey.id,
    journey.owner.id,
    journey.primaryExpert?.id,
    onSuccess,
    templateId,
    title,
    type,
    updateGoal
  ]);

  return (
    <Form className={styles.editorForm} hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={addingGoal ? '' : 'ion-hide'}>
        <IonSkeletonText animated className={styles.loader} />
      </div>
      <div className={addingGoal ? 'ion-hide' : ''}>
        {canTemplate && (
          <SearchInput
            error={''}
            initialValue={undefined}
            name="templateId"
            noSpacing
            onSearch={searchTemplates}
            placeholder={intl.formatMessage({ id: templatePlaceholderKey })}
            setValue={v => {
              setTemplateId(v);
            }}
          />
        )}
        <div className={styles.browsers}>
          {!goal && itemBrowserOpener && (
            <ItemBrowser
              handleAdd={(id, title) => handleAddTemplate(id, title)}
              handleRemove={() => {}}
              itemType="GOAL"
              journeyId={journey.id}
              opener={itemBrowserOpener}
            />
          )}
          {type && goal && itemBrowserOpener && (
            <ItemBrowser
              handleAdd={(id, title) => handleAddTemplate(id, title)}
              handleRemove={() => {}}
              itemType={type}
              journeyId={journey.id}
              opener={itemBrowserOpener}
            />
          )}
        </div>
        <div className={type === GoalItemTypeEnum.TASK ? styles.labelAndDateInput : 'ion-hide'}>
          <div className={styles.dateLabel}>
            <BodyCopy color="neutral500">
              <FormattedMessage id="forms.task.dueTimeLabel" />
            </BodyCopy>
          </div>
          <DateInput
            control={form.control}
            error={form.formState.errors.dueTime}
            futureOnly
            name="dueTime"
            required
            skipSpacing
          />
        </div>
        <div className={styles.relativeContainer}>
          <RichTextArea<FormData>
            contentRefreshSwitch={editorRefresh}
            control={form.control}
            error={form.formState.errors.title}
            extraStyles={type ? styles.smallTextEditor : styles.editor}
            journeyId={journey.id}
            name="title"
            placeholder={
              !goal
                ? intl.formatMessage({ id: 'pages.carePlan.base.goals.typeGoalName' })
                : intl.formatMessage({ id: 'pages.carePlan.base.goals.typeTaskName' })
            }
            preventEnter
            sharableURLSHidden={type !== GoalItemTypeEnum.CARE_PLAN_ACTION_ITEM}
            size="small"
            textMenuHidden
          />
          <div className={styles.submitIcons}>
            <CircleIconButton
              ariaLabelKey="accessibility.ariaLabels.add"
              color="success300"
              data-test={type ? `goal-editor-submit-${type}` : 'goal-editor-submit'}
              disabled={!form?.formState.isValid || form?.formState.isSubmitting || submitDisabled}
              icon="checkmark"
              type="submit"
            />
            <CircleIconButton
              ariaLabelKey="accessibility.ariaLabels.delete"
              color="error300"
              icon="xmark"
              onClick={() => {
                setTemplateId(undefined);
                form.reset();
                onCancel?.();
              }}
            />
          </div>
        </div>
      </div>
      <div className={type === GoalItemTypeEnum.TASK ? '' : 'ion-hide'}></div>
    </Form>
  );
};

export default GoalEditorTextField;
